import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { formatCount } from "../../utils/deviceTracker";
import "./ProjectLikes.css";

// Import local fallback project images if available
import opsMindImage from "../../assets/images/OpsMind AI.png";
import aiAdCreativeImage from "../../assets/images/ai ad creative.png";
import employeeImage from "../../assets/images/employeedailytask .jpeg";
import convertersImage from "../../assets/images/Converters App Image.png";
import notesAppImage from "../../assets/images/Pk Notes App.png";
import rfidImage from "../../assets/images/RFID Image.png";
import smartExpenseImage from "../../assets/images/SmartExpense Photo.png";
import vistaHolidaysImage from "../../assets/images/Vista holidays Hero.png";
import todoImage from "../../assets/images/To-Do-List-App Image.png";
import aiPlatformImage from "../../assets/images/AI Powered Communication Platform.avif";
import cloudVendorImage from "../../assets/images/CloudVendor App Image.png";
import ticTacToeImage from "../../assets/images/tic-tac-toe App Image.png";
import easyHubImage from "../../assets/images/easyhub.png";
import aeroGlassImage from "../../assets/images/aeroglass.png";
import vortexImage from "../../assets/images/vorteximage.png";
import neonBiteImage from "../../assets/images/neonbite.png";
import tapAcademyImage from "../../assets/images/tapacademy.png";
import instagramCloneImage from "../../assets/images/instagramuiclone.png";
import spotifyCloneImage from "../../assets/images/spotifyuiclone.png";
import linkedinCloneImage from "../../assets/images/linkedinuiclone.png";
import netflixCloneImage from "../../assets/images/netflixuiclone.png";

const fallbackImageMap = {
  opsMindAI: opsMindImage,
  aiAdCreativeGenerator: aiAdCreativeImage,
  employeeTaskManager: employeeImage,
  convertersApp: convertersImage,
  notesApp: notesAppImage,
  smartExpense: smartExpenseImage,
  vistaHolidays: vistaHolidaysImage,
  rfidBusSystem: rfidImage,
  todoApp: todoImage,
  aiCommunicationPlatform: aiPlatformImage,
  cloudVendorApp: cloudVendorImage,
  ticTacToe: ticTacToeImage,
  easyHub: easyHubImage,
  aeroGlassPage: aeroGlassImage,
  vortexFest: vortexImage,
  neonBite: neonBiteImage,
  tapAcademy: tapAcademyImage,
  instagramClone: instagramCloneImage,
  spotifyClone: spotifyCloneImage,
  linkedinClone: linkedinCloneImage,
  netflixClone: netflixCloneImage
};

const fallbackTitleMap = {
  opsMindAI: "OpsMind AI",
  aiAdCreativeGenerator: "AI Ad Creative Generator",
  employeeTaskManager: "Employee Daily Task Allocation Management System",
  convertersApp: "Converters App Project",
  notesApp: "PK Notes App",
  smartExpense: "SmartExpense",
  vistaHolidays: "Vista Holidays",
  rfidBusSystem: "RFID Based Bus Number Announce System For Blind",
  todoApp: "To-Do List App",
  aiCommunicationPlatform: "AI Powered Communication Platform for Deaf and Mute",
  cloudVendorApp: "CloudVendor App using Java Spring Boot & MySQL JDBC",
  ticTacToe: "Tic-Tac-Toe App",
  easyHub: "Easy Hub",
  aeroGlassPage: "AeroGlass Product Landing Page",
  vortexFest: "Vortex College Fest Website",
  neonBite: "Neon Bite Food Delivery",
  tapAcademy: "Tap Academy Clone",
  instagramClone: "Instagram UI Clone",
  spotifyClone: "Spotify UI Clone",
  linkedinClone: "LinkedIn UI Clone",
  netflixClone: "Netflix UI Clone"
};

// Format camelCase docId to clean Title Case if no name is in Firestore
const formatDocIdToName = (docId) => {
  if (fallbackTitleMap[docId]) return fallbackTitleMap[docId];
  return docId
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

function ProjectLikes() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCumulativeLikes, setTotalCumulativeLikes] = useState(0);
  const [totalCumulativeViews, setTotalCumulativeViews] = useState(0);

  const fetchProjectLikes = async () => {
    setLoading(true);
    try {
      const projectsRef = collection(db, "projects");
      const projectsSnap = await getDocs(projectsRef);
      const projectList = [];
      let totalLikesSum = 0;
      let totalViewsSum = 0;

      projectsSnap.forEach((docSnap) => {
        const data = docSnap.data();
        const docId = docSnap.id;
        const likesCount = data.likes || 0;
        const viewsCount = data.views || 0;

        totalLikesSum += likesCount;
        totalViewsSum += viewsCount;

        const name = data.name || data.title || formatDocIdToName(docId);
        const image = data.image || data.imgUrl || data.imageUrl || fallbackImageMap[docId] || null;

        projectList.push({
          id: docId,
          name,
          likes: likesCount,
          views: viewsCount,
          image,
          category: data.category || "Project"
        });
      });

      // Sort projects by likes descending
      projectList.sort((a, b) => b.likes - a.likes);

      setProjects(projectList);
      setTotalCumulativeLikes(totalLikesSum);
      setTotalCumulativeViews(totalViewsSum);
    } catch (error) {
      console.error("Error fetching project likes from Firebase:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectLikes();
  }, []);

  return (
    <div className="project-likes-container">
      {/* PAGE HEADER */}
      <div className="page-header-row">
        <div>
          <h2>❤️ Praveen's Project Likes</h2>
          <p className="page-sub">
            Real-time project appreciation and view counts directly from Firebase "projects" collection
          </p>
        </div>

        <button className="btn-refresh" onClick={fetchProjectLikes} disabled={loading}>
          🔄 Refresh
        </button>
      </div>

      {/* SUMMARY BANNER */}
      <div className="dash-glass-panel likes-summary-panel">
        <div className="summary-info-item">
          <span className="summary-icon">📂</span>
          <div>
            <span className="summary-label">Total Projects</span>
            <span className="summary-value">{projects.length}</span>
          </div>
        </div>

        <div className="summary-info-item">
          <span className="summary-icon">❤️</span>
          <div>
            <span className="summary-label">Total Cumulative Likes</span>
            <span className="summary-value highlight-likes">{totalCumulativeLikes.toLocaleString()}</span>
          </div>
        </div>

        <div className="summary-info-item">
          <span className="summary-icon">👀</span>
          <div>
            <span className="summary-label">Total Cumulative Views</span>
            <span className="summary-value highlight-views">{totalCumulativeViews.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* PROJECT CARDS GRID */}
      {loading ? (
        <div className="loading-spinner-box" style={{ padding: "80px", textAlign: "center" }}>
          <div className="spinner"></div>
          <p style={{ color: "#38bdf8", marginTop: "12px" }}>Fetching Project Likes & Views from Firebase...</p>
        </div>
      ) : projects.length > 0 ? (
        <div className="project-likes-grid">
          {projects.map((project) => (
            <div key={project.id} className="project-like-card glass-glow">
              {project.image ? (
                <div className="card-image-wrapper">
                  <img src={project.image} alt={project.name} className="project-card-img" />
                </div>
              ) : (
                <div className="card-image-placeholder">
                  <span>🚀</span>
                </div>
              )}

              <div className="project-card-body">
                <h3 className="project-card-title">{project.name}</h3>

                <div className="card-badges-row">
                  <div className="project-card-like-badge">
                    <span className="heart-icon">❤️</span>
                    <span className="likes-text">Likes : </span>
                    <span className="likes-number">{formatCount(project.likes)}</span>
                  </div>

                  <div className="project-card-views-badge">
                    <span className="eye-icon">👀</span>
                    <span className="views-text">Views : </span>
                    <span className="views-number">{formatCount(project.views)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>No projects found in the Firebase "projects" collection.</p>
        </div>
      )}
    </div>
  );
}

export default ProjectLikes;
